import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Loading from "../components/Loading";
import "./ProductLogsPage.css";
import { apiFetch } from "../components/api";

const ACTIONS = [
  { v: "IN", l: "입고" },
  { v: "OUT", l: "출고" },
  { v: "NEW", l: "신규" },
  { v: "SPL_IN", l: "분리입고" },
  { v: "SPL_OUT", l: "분리출고" },
  { v: "CHECK", l: "재고조사" },
  { v: "STATE", l: "상태변경" },
  { v: "LOCATION", l: "위치변경" },
  { v: "LOCATION_CHECK", l: "위치확인" },
];

function ProductLogsPage() {
  const { productId } = useParams();

  // ✅ 다중 선택: [] = 전체
  const [actionFilters, setActionFilters] = useState([]);
  const [draftActions, setDraftActions] = useState([]);
  const [actionOpen, setActionOpen] = useState(false);

  // ✅ 기간(YYYY-MM-DD)
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [draftStartDate, setDraftStartDate] = useState("");
  const [draftEndDate, setDraftEndDate] = useState("");

  const [logs, setLogs] = useState([]);
  const [rawLogs, setRawLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [productName, setProductName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getRowClass = (actionDisplay) => {
    const s = String(actionDisplay || "").trim();
    if (s === "입고") return "row-in";
    if (s === "출고") return "row-out";
    if (s === "재고조사") return "row-audit";
    if (s === "상태변경") return "row-state";
    if (s === "위치변경") return "row-location";
    if (s === "위치확인") return "row-audit";
    return "";
  };

  const formatDateTime = (ts) => {
    const d = new Date(ts);
    return { date: d.toLocaleDateString(), time: d.toLocaleTimeString() };
  };

  const labelOf = (v) => ACTIONS.find(a => a.v === v)?.l || v;
  const actionLabel = (() => {
    if (actionFilters.length === 0) return "전체";
    const first = labelOf(actionFilters[0]);
    if (actionFilters.length === 1) return first;
    return `${first} 외 ${actionFilters.length - 1}`;
  })();


  const buildUrl = () => {
    const qs = new URLSearchParams();
    // actionFilters.forEach((a) => qs.append("action", a));
    if (startDate) qs.set("start", startDate);
    if (endDate) qs.set("end", endDate);
    // qs.set("limit", "500");

    const base = productId
      ? `/api/inventory/logs/${productId}/`
      : `/api/inventory/logs/`;

    return `${base}?${qs.toString()}`;
  };

  const filteredLogs =
    actionFilters.length === 0
      ? rawLogs
      : rawLogs.filter((log) => actionFilters.includes(String(log.action || "").trim()));
  
  const showProductCol = !productId;

  const showInCols =
    (actionFilters.length === 1 && (actionFilters[0] === "IN" || actionFilters[0] === "NEW")) ||
    (actionFilters.length === 2 &&
      ((actionFilters[0] === "IN" && actionFilters[1] === "NEW") ||
      (actionFilters[0] === "NEW" && actionFilters[1] === "IN")));

  const navigate = useNavigate();

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        setError(null);

        const url = buildUrl();
        const data = await apiFetch(url, { cache: "no-store" });

        const items = Array.isArray(data.items) ? data.items : [];
        setRawLogs(items);
        setTotal(Number.isFinite(data.total) ? data.total : (Number.isFinite(data.count) ? data.count : items.length));
        setProductName(data.product?.name || "");
      } catch (e) {
        console.error(e);
        setError(`이력 로드 에러 : ${e?.message || "알 수 없는 오류"}`);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [productId, startDate, endDate]);

  useEffect(() => {
    if (!actionOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") cancel();
      if (e.key === "Enter") apply(); // 엔터도 조회로
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [actionOpen, draftActions, draftStartDate, draftEndDate, actionFilters, startDate, endDate]);

  useEffect(() => {
    if (!actionOpen) return;
    setDraftActions(actionFilters);
    setDraftStartDate(startDate);
    setDraftEndDate(endDate);
  }, [actionOpen, actionFilters, startDate, endDate]);

  const toggleDraftAction = (v) => {
    setDraftActions((prev) => (
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
    ));
  };

  const setDraftAll = () => setDraftActions([]);

  const apply = () => {
    setActionFilters(draftActions);
    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
    setActionOpen(false);
  };

  const cancel = () => {
    setDraftActions(actionFilters);
    setDraftStartDate(startDate);
    setDraftEndDate(endDate);
    setActionOpen(false);
  };

  const toNum = (v) => {
    if (v === null || v === undefined) return NaN;
    const s = String(v).replaceAll(",", "").trim();
    const n = Number(s);
    return Number.isFinite(n) ? n : NaN;
  };

  const avgUnitPrice = (() => {
    if (!showInCols) return null;

    let sumQty = 0;
    let sumCost = 0;

    for (const log of filteredLogs) {
      const action = String(log.action || "").trim();
      if (action !== "IN" && action !== "NEW") continue;

      const qty = toNum(log.quantity_change);
      const price = toNum(log.purchase_price);
      if (!Number.isFinite(qty) || qty <= 0) continue;
      if (!Number.isFinite(price) || price < 0) continue;

      sumQty += qty;
      sumCost += qty * price;
    }

    if (sumQty <= 0) return null;
    return sumCost / sumQty;
  })();

  return (
    <div className="logs-page">
      <Loading active={loading} />

      <div className="header">
        <div className="header-left">
          <h1
            className="title"
            onClick={() => navigate("/logs")}
            role="button"
            tabIndex={0}
          >
            재고 이력
            {productName && <span className="title-sub"> - {productName}</span>}
          </h1>
        </div>

        <div className="header-right">
          {/* ✅ 기간 */}
          <input
            className="date-input"
            type="date"
            value={draftStartDate}
            onChange={(e) => setDraftStartDate(e.target.value)}
          />
          <span className="date-tilde">~</span>
          <input
            className="date-input"
            type="date"
            value={draftEndDate}
            onChange={(e) => setDraftEndDate(e.target.value)}
          />

          {/* ✅ 다중 action 드롭다운 */}
          <div className="multi">
            <button
              type="button"
              className="filter-select multi-btn"
              onClick={() => setActionOpen((v) => !v)}
            >
              {actionLabel}
            </button>

            {actionOpen && (
              <div className="multi-pop">
                <label className="multi-item">
                  <input
                    type="checkbox"
                    checked={draftActions.length === 0}
                    onChange={setDraftAll}
                  />
                  전체
                </label>

                {ACTIONS.map((a) => (
                  <label key={a.v} className="multi-item">
                    <input
                      type="checkbox"
                      checked={draftActions.includes(a.v)}
                      onChange={() => toggleDraftAction(a.v)}
                    />
                    {a.l}
                  </label>
                ))}
              </div>
            )}
          </div>

          <button className="blue-btn" type="button" onClick={apply}>
            검색
          </button>
          <div>
            총 {total}건 / 표시 {filteredLogs.length}건
          </div>
        </div>
      </div>

      <div className="body">
        {error && !loading && <div className="status status-error">{error}</div>}

        {!loading && !error && filteredLogs.length === 0 && (
          <div className="status">이력이 없습니다.</div>
        )}

        {!loading && !error && filteredLogs.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>시간</th>
                  {showProductCol && <th>모델명</th>}
                  <th>유형</th>
                  <th>변경</th>
                  <th>재고</th>
                  {showInCols && <th>구매처</th>}
                  {showInCols && <th>단가</th>}
                  <th>상태</th>
                  <th>위치</th>
                  <th>계정</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((log) => (
                  <tr
                    key={log.id}
                    className={getRowClass(log.action_display || log.action)}
                  >
                    <td>
                      {(() => {
                        const t = formatDateTime(log.timestamp);
                        return (
                          <>
                            <div className="date-sub">{t.date}</div>
                            <div className="time-sub">{t.time}</div>
                          </>
                        );
                      })()}
                    </td>
                    {showProductCol && (() => {
                      const pid = log.product_id;
                      const name = log.product_name || "-";
                      const deleted = !pid;

                      return (
                        <td
                          className={deleted ? "cell-deleted" : "cell-link"}
                          onClick={deleted ? undefined : () => navigate(`/logs/${pid}`)}
                        >
                          {deleted ? `${name} (삭제됨)` : name}
                        </td>
                      );
                    })()}
                    <td>{log.action_display}</td>
                    <td>{log.quantity_change}</td>
                    <td>{log.final_stock}</td>
                    {showInCols && <td>{log.purchase_supplier_name || "-"}</td>}
                    {showInCols && <td>{log.purchase_price || "-"}</td>}
                    <td>{log.state_name || "-"}</td>
                    <td>{log.location_name || "-"}</td>
                    <td>{log.account_username || "-"}</td>
                  </tr>
                ))}

                {showInCols && filteredLogs.length > 0 && (
                  <tr className="row-summary">
                    <td colSpan={showProductCol ? 5 : 4} className="summary-label">
                      평균단가
                    </td>
                    <td className="summary-supplier">-</td>
                    <td className="summary-price">
                      {avgUnitPrice === null ? "-" : Math.round(avgUnitPrice).toLocaleString()}
                    </td>
                    <td colSpan={3}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductLogsPage;